import React from "react";
import "./App.css";
import { useSelector } from "react-redux";

import {
  Card,
  CardContent,
  Container,
  Typography,
  Box,
  InputAdornment,
  FormControl,
  FilledInput,
  InputLabel,
  Button,
  Tooltip,
} from "@mui/material";

import AppToolbar from "./components/AppToolbar";
import CheckoutDialog from "./components/Checkout";
import Calendar from "./components/Calendar";
import SuspendedOverlay from "./components/SuspendedOverlay";
import { YEAR, EVENT_TITLE, DATES, BOOKING_SUSPENDED } from "./config";
import { priceBooking } from "./pricing";
import { watchBookedSets } from "./inventory";
import { database } from "./firebaseConfig";

const emptySets = () => DATES.map(() => 0);

const formatDateRange = (dates) => {
  const month = (d) =>
    new Intl.DateTimeFormat("en-US", { month: "long", timeZone: "UTC" }).format(
      new Date(d),
    );
  const day = (d) => new Date(d).getUTCDate();
  const first = dates[0];
  const last = dates[dates.length - 1];
  return month(first) === month(last)
    ? `${month(first)} ${day(first)}–${day(last)}`
    : `${month(first)} ${day(first)} – ${month(last)} ${day(last)}`;
};

const MyCards = () => {
  const { user } = useSelector((state) => state.auth);
  const [open, setOpen] = React.useState(false);
  const [sets, setSets] = React.useState(emptySets);
  const [total, setTotal] = React.useState(0);
  const [totals, setTotals] = React.useState(emptySets);
  const [discounts, setDiscounts] = React.useState(emptySets);
  const [discountedTotals, setDiscountedTotals] = React.useState(emptySets);
  const [booked, setBooked] = React.useState(null);

  React.useEffect(() => watchBookedSets(database, setBooked), []);

  const toggleCheckout = () => setOpen((prev) => !prev);

  const cart = {
    dates: DATES,
    sets,
    totals,
    discounts,
    discountedTotals,
  };

  React.useEffect(() => {
    const savedCart = user?.[`cart${YEAR}`];
    if (savedCart?.sets) setSets(savedCart.sets);
  }, [user]);

  React.useEffect(() => {
    const priced = priceBooking(sets);
    setTotal(priced.total);
    setTotals(priced.totals);
    setDiscounts(priced.discounts);
    setDiscountedTotals(priced.discountedTotals);
  }, [sets]);

  const handleChangeSets = (index, value) => {
    const newSets = [...sets];
    newSets[index] = value;
    setSets(newSets);
  };

  const handleChange = (e, index) => {
    const { value } = e.target;
    if (value < 0) return handleChangeSets(index, "");
    if (isNaN(value)) return handleChangeSets(index, "");
    return handleChangeSets(
      index,
      value ? parseInt(value.replace(/^0+([1-9])/, "$1")) : 0,
    );
  };

  const reserveButton = (
    <Button fullWidth variant="contained" disabled>
      Reserve Sets
    </Button>
  );

  const bookingArea = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Calendar
        dates={DATES}
        handleChange={handleChange}
        sets={sets}
        booked={booked}
      />
      <Typography variant="caption" color="text.secondary">
        1 Set = 2 Chairs & 1 Umbrella
      </Typography>
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h5" align="center" sx={{ mb: 2 }}>
            Total
          </Typography>
          <FormControl
            sx={{ maxWidth: 160, "& input": { textAlign: "center" } }}
            variant="filled"
          >
            <InputLabel htmlFor="filled-adornment-amount">Amount</InputLabel>
            <FilledInput
              id="filled-adornment-amount"
              value={total.toFixed(2)}
              readOnly
              startAdornment={<InputAdornment position="start">$</InputAdornment>}
            />
          </FormControl>
        </CardContent>
        <Box sx={{ px: 3, pb: 3 }}>
          {user && user.uid ? (
            user.building ? (
              <Button fullWidth variant="contained" onClick={toggleCheckout}>
                Reserve Sets
              </Button>
            ) : (
              <Tooltip title="Update Info to Continue (Top Right)">
                <span>{reserveButton}</span>
              </Tooltip>
            )
          ) : (
            <Tooltip title="Sign in to Reserve Sets (Top Right)">
              <span>{reserveButton}</span>
            </Tooltip>
          )}
        </Box>
      </Card>
    </Box>
  );

  return (
    <Container maxWidth="sm" sx={{ pb: 6 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 4 }}>
        <Typography variant="h5" color="primary.dark">
          {EVENT_TITLE}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {formatDateRange(DATES)} · Tops'l Beach
        </Typography>
        {BOOKING_SUSPENDED ? (
          <SuspendedOverlay>{bookingArea}</SuspendedOverlay>
        ) : (
          bookingArea
        )}
        {!BOOKING_SUSPENDED && (
          <CheckoutDialog
            cart={cart}
            total={total}
            open={open}
            onClose={toggleCheckout}
          />
        )}
      </Box>
    </Container>
  );
};

function App() {
  return (
    <Box className="App">
      <AppToolbar />
      <MyCards />
    </Box>
  );
}

export default App;
